import { useQuery } from '@tanstack/react-query';
import { api, type Params } from '../api/client';
import type { LatestRate, Option, Row } from '../api/types';

/** Dropdown lists from /api/lookups/* (any signed-in user may read them). Cached for a minute. */
export function useLookup<T = Row>(name: string, params?: Params) {
  return useQuery({
    queryKey: ['lookup', name, params ?? {}],
    queryFn: () => api.get<T[]>(`/lookups/${name}`, params),
    staleTime: 60_000,
  });
}

export function useLatestRates(date?: string) {
  return useQuery({
    queryKey: ['lookup', 'exchange-rates/latest', date ?? 'today'],
    queryFn: () => api.get<LatestRate[]>('/lookups/exchange-rates/latest', { date }),
    staleTime: 60_000,
  });
}

export const toOptions = <T extends Row>(rows: T[] | undefined, label: (r: T) => string = (r) => String(r.name), value: (r: T) => string | number = (r) => r.id): Option[] =>
  (rows ?? []).map((r) => ({ value: String(value(r)), label: label(r) }));

/**
 * The installation's base currency: the one flagged "Base currency" on the Currency screen.
 * `undefined` while the currency list is loading, `null` once loaded when none is flagged.
 */
export function useBaseCurrency(): { code: string; name: string } | null | undefined {
  const currencies = useLookup('currencies');
  if (!currencies.data) return undefined;
  const base = currencies.data.find((c) => c.isBase);
  return base ? { code: String(base.code), name: String(base.name) } : null;
}
